/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Textarea.js
 *
 * Purpose: Renders the multi-line text input used by FormV2 for TEXT field types.
 *
 * Scope: FormV2 input rendering for textarea entity fields.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Textarea, a memoized component that registers a `<textarea>` with
 *     react-hook-form via the passed-in `register(name, { required })` function, applying
 *     read-only styling and readOnlyAttrs when the field is read-only. Reuses Input's
 *     transform strategy unchanged for server/client value conversion.
 *
 * Exports: default Textarea component (with an attached `transform` static property).
 */

import React from 'react';
import classNames from 'classnames';
import { domProps } from 'shared/utils/form';
import Input, { readOnlyAttrs } from './Input';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer: Input.transform.fromServer,
  toServer: Input.transform.toServer,
  defaultValue: Input.transform.defaultValue,
};

const Textarea = React.memo(function Textarea({
  name,
  register,
  displayName,
  required = false,
  readOnly,
  ...props
}) {
  return (
    <textarea
      id={name}
      className={classNames(
        'w-100 h-input p-2 mb-4 fs-7 fw-normal lh-sm border-0 border-bottom border-input',
        readOnly && 'bg-light text-muted cursor-not-allowed'
      )}
      placeholder={displayName}
      {...register(name, { required })}
      {...(readOnly && readOnlyAttrs)}
      {...domProps(props)}
    />
  );
});

Textarea.transform = transform;
export default Textarea;
